// src/pages/Contact.js
// ──────────────────────────────────────────────────────────────────────────
// Contact & enquiry — themed. Real MTI details + an enquiry form wired to
// your existing ContactAPI.submit (src/api/contact.js). If the backend isn't
// ready it falls back to a mailto link so messages still reach you.
// ──────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactAPI from '../api/contact';
import { SAMPLE_PROGRAMS } from '../api/programs';
import { useSiteContent } from '../context/SiteContentContext';
import theme from '../theme/theme';

const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff;
  padding: 7rem ${theme.layout.gutter} 5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2.4rem, 6vw, 4rem); color: #fff; margin-top: 1.2rem; }
  p { margin-top: 1rem; max-width: 50ch; font-weight: 300; color: rgba(255,255,255,0.82); }
  .kicker { color: #fff; }
`;

const Wrap = styled.section`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 5rem ${theme.layout.gutter};
  display: grid; grid-template-columns: 0.8fr 1.2fr; gap: 4rem; align-items: start;
  @media (max-width: 860px) { grid-template-columns: 1fr; gap: 2.5rem; }
`;

const Details = styled.div`
  .row { padding: 1.1rem 0; border-bottom: 1px solid ${theme.color.line}; }
  .row h4 { font-family: ${theme.font.body}; font-size: 0.72rem; letter-spacing: 0.16em; text-transform: uppercase; color: ${theme.color.accent}; margin-bottom: 0.4rem; }
  .row p, .row a { margin: 0; color: ${theme.color.ink}; }
  .row a:hover { color: ${theme.color.accent}; }
`;

const Form = styled.div`
  label { display: block; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: ${theme.color.inkSoft}; margin: 1rem 0 0.4rem; }
  input, select, textarea {
    width: 100%; font-family: ${theme.font.body}; font-size: 0.98rem;
    padding: 11px 14px; border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius};
    background: #fff; color: ${theme.color.ink};
    &:focus { outline: none; border-color: ${theme.color.accent}; }
  }
  textarea { min-height: 130px; resize: vertical; }
  .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; @media (max-width: 520px) { grid-template-columns: 1fr; } }
  button {
    margin-top: 1.6rem; background: ${theme.color.accent}; color: ${theme.color.onAccent};
    border: none; font-family: ${theme.font.body}; font-size: 0.9rem; font-weight: 700;
    padding: 13px 28px; border-radius: ${theme.layout.radius}; cursor: pointer;
    &:hover { background: ${theme.color.accentDark}; }
    &:disabled { opacity: 0.6; cursor: default; }
  }
  .note { margin-top: 1rem; font-size: 0.9rem; }
  .ok { color: #1a7f4b; } .err { color: #b3261e; }
`;

function Contact() {
  const contact = useSiteContent('contact') || theme.info;
  const [form, setForm] = useState({ name: '', email: '', phone: '', course: '', message: '' });
  const [status, setStatus] = useState(null); // 'sending' | 'ok' | 'err'

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setStatus('sending');
    try {
      await ContactAPI.submit({
        name: form.name, email: form.email, phone: form.phone,
        projectType: form.course, budget: '', brief: form.message,
      });
      setStatus('ok');
      setForm({ name: '', email: '', phone: '', course: '', message: '' });
    } catch {
      setStatus('err');
    }
  };

  const mailto = `mailto:${contact?.email || 'mbalainstitute@gmail.com'}?subject=${encodeURIComponent('Course enquiry')}&body=${encodeURIComponent(`Name: ${form.name}\nPhone: ${form.phone}\nCourse: ${form.course}\n\n${form.message}`)}`;

  return (
    <>
      <Navbar />
      <Header>
        <div className="inner">
          <span className="kicker">Get in touch</span>
          <h1>Contact &amp; enquiries</h1>
          <p>Ask about courses, intakes or our services. We’ll help you find the right starting point.</p>
        </div>
      </Header>

      <Wrap>
        <Details>
          <span className="kicker">Visit us</span>
          <div style={{ marginTop: '1rem' }}>
            <div className="row"><h4>Address</h4><p>{contact?.address}</p></div>
            <div className="row"><h4>Postal</h4><p>{contact?.postal}</p></div>
            <div className="row"><h4>Email</h4><a href={`mailto:${contact?.email}`}>{contact?.email}</a></div>
            {contact?.phone && <div className="row"><h4>Phone</h4><a href={`tel:${contact.phone.replace(/\s/g,'')}`}>{contact.phone}</a></div>}
          </div>
        </Details>

        <Form>
          <span className="kicker">Send an enquiry</span>
          <div className="row2">
            <div><label>Full name</label><input value={form.name} onChange={set('name')} /></div>
            <div><label>Phone</label><input value={form.phone} onChange={set('phone')} /></div>
          </div>
          <label>Email</label>
          <input type="email" value={form.email} onChange={set('email')} />
          <label>Course of interest</label>
          <select value={form.course} onChange={set('course')}>
            <option value="">Select a course (optional)</option>
            {SAMPLE_PROGRAMS.map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
          </select>
          <label>Message</label>
          <textarea value={form.message} onChange={set('message')} placeholder="Tell us a little about what you'd like to learn…" />

          <button onClick={submit} disabled={status === 'sending' || !form.name || !form.email}>
            {status === 'sending' ? 'Sending…' : 'Send enquiry'}
          </button>

          {status === 'ok'  && <p className="note ok">Thank you — your enquiry has been sent. We’ll be in touch.</p>}
          {status === 'err' && (
            <p className="note err">
              We couldn’t send it automatically. Please email us directly at{' '}
              <a href={mailto}>{contact?.email || 'mbalainstitute@gmail.com'}</a>.
            </p>
          )}
        </Form>
      </Wrap>

      <Footer />
    </>
  );
}

export default Contact;
